import React, { useState, useEffect, useRef } from 'react';
import { BillData, BillItem, Company } from '../types/billing';

interface BillGeneratorProps {
  companies: Company[];
  onSave: (billData: BillData) => void;
  onClose: () => void;
  initialData?: Partial<BillData>;
  isSaving?: boolean;
}

const BillGenerator: React.FC<BillGeneratorProps> = ({
  companies,
  onSave,
  onClose,
  initialData,
  isSaving = false
}) => {
  const [billData, setBillData] = useState<BillData>({
    companyName: 'AVI\'S ENGINEERING & CONSULTANCY',
    companySubtitle: 'NDT SERVICES',
    companyAddress: 'SR No-4/1A; plot on-: 17 SHREE SWAMI SAMARTH NIWAS, Bistabag, Savedi, Ahilyanagar',
    companyPhone: '7887985350',
    companyEmail: 'avis.engineering2025@gmail.com',
    companyPAN : '',
    companyGSTN: '27FLTPM3802F1ZU',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().slice(0,10),
    workOrderNumber: 'N/A',
    workOrderDate: 'N/A',
    periodOfWork: '',
    clientId: '',
    clientName: '',
    clientAddress: '',
    clientGSTN: '',
    clientState: 'Maharashtra',
    clientStateCode: '27',
    clientEmail: '',
    clientCompanyName: '',
    items: [
      { srNo: 1, description: 'NDT Testing Charges', sac: '998399', qty: 1, rate: 0, totalAmount: 0 }
    ],
    gstRate: 0.18,
    igstAmount: 0,
    subTotal: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    totalGstAmount: 0,
    totalAmount: 0,
    bankDetails: {
      bankName: 'SARASWAT CO-OPERATIVE BANK LTD.',
      branch: 'MIDC AHMEDNAGAR',
      accountNumber: '610000000061452',
      ifscCode: 'SRCB0000400'
    },
    termsAndConditions: [
      'Subject to Ahmednagar Jurisdiction only.',
      'All payments are delayed more than 30 days from the date of submission of invoice along with test reports then interest at the rate of 24% will be charged extra on all over dues.',
      'I.S.O. Certificate will be forwarded immediately.'
    ],
    status: 'Draft',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...initialData
  });

  const [selectedClient, setSelectedClient] = useState<Company | null>(
    companies[0] || null
  );
  const [companySearchQuery, setCompanySearchQuery] = useState(
    companies[0]?.name ?? ''
  );
  const [companyListOpen, setCompanyListOpen] = useState(false);
  const companySearchRef = useRef<HTMLDivElement>(null);

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(companySearchQuery.toLowerCase().trim())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (companySearchRef.current && !companySearchRef.current.contains(event.target as Node)) {
        setCompanyListOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedClient) {
      setCompanySearchQuery(selectedClient.name);
    }
  }, [selectedClient?.id]);

  useEffect(() => {
    if (companies[0] && !selectedClient) {
      setSelectedClient(companies[0]);
      setCompanySearchQuery(companies[0].name);
    }
  }, [companies.length]);

  useEffect(() => {
    if (selectedClient) {
      setBillData(prev => ({
        ...prev,
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        clientAddress: selectedClient.address,
        clientGSTN: selectedClient.gstn,
        clientState: selectedClient.state,
        clientStateCode: selectedClient.stateCode,
        clientEmail: selectedClient.email,
        clientCompanyName: selectedClient.name,
      }));

      // Generate invoice number for the selected client if one doesn't exist
      if (!billData.invoiceNumber || billData.invoiceNumber.trim() === '') {
        generateInvoiceNumberForClient(selectedClient.id);
      }
    }
  }, [selectedClient, billData.invoiceNumber]);

  const generateInvoiceNumberForClient = async (clientId: string) => {
    try {
      console.log(`BillGenerator: Generating invoice number for client ${clientId}`);
      const { generateNextInvoiceNumber } = await import('../services/firebaseBilling');
      const invoiceNumber = await generateNextInvoiceNumber(clientId);
      console.log(`BillGenerator: Generated invoice number: ${invoiceNumber}`);
      setBillData(prev => ({
        ...prev,
        invoiceNumber: invoiceNumber
      }));
    } catch (error) {
      console.error('BillGenerator: Error generating invoice number:', error);
      // Set a temporary invoice number if generation fails
      setBillData(prev => ({
        ...prev,
        invoiceNumber: '1'
      }));
    }
  };

  const formatNumber = (num: number): string => {
    return Math.round(num).toString();
  };

  // Round amount to 2 decimal places for intermediate calculations
  const roundAmount = (num: number): number => {
    return Math.round(num * 100) / 100;
  };

  // Round to whole number (same as bill PDF - >= 0.50 rounds up, < 0.50 rounds down)
  const roundToWhole = (num: number): number => {
    return Math.round(num);
  };

  const calculateTotals = (items: BillItem[], gstRate: number) => {
    const subTotal = roundAmount(items.reduce((sum, item) => sum + roundAmount(item.totalAmount), 0));
    const totalGstAmount = roundAmount(subTotal * gstRate);
    const cgstAmount = roundAmount(totalGstAmount / 2);
    const sgstAmount = roundAmount(totalGstAmount / 2);
    const totalBeforeRound = subTotal + totalGstAmount;
    // Round final total to whole number (same as bill PDF)
    const totalAmount = roundToWhole(totalBeforeRound);
    return { subTotal, cgstAmount, sgstAmount, totalGstAmount, totalAmount };
  };

  const handleBillDataChange = (key: keyof BillData, value: string | number) => {
    setBillData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleItemChange = (index: number, key: keyof BillItem, value: string | number) => {
    const updatedItems = [...billData.items];
    const itemToUpdate = updatedItems[index];
    
    if (key === 'qty' || key === 'rate') {
      const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      (itemToUpdate as any)[key] = numValue;
      itemToUpdate.totalAmount = roundAmount(itemToUpdate.qty * itemToUpdate.rate);
    } else {
      (itemToUpdate as any)[key] = value;
    }

    const totals = calculateTotals(updatedItems, billData.gstRate);
    setBillData(prev => ({
      ...prev,
      items: updatedItems,
      ...totals,
    }));
  };

  const addItem = () => {
    const newItem: BillItem = {
      srNo: billData.items.length + 1,
      description: '',
      sac: '998399',
      qty: 1,
      rate: 0,
      totalAmount: 0
    };
    setBillData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index: number) => {
    if (billData.items.length > 1) {
      const updatedItems = billData.items.filter((_, i) => i !== index);
      const renumberedItems = updatedItems.map((item, i) => ({
        ...item,
        srNo: i + 1
      }));
      const totals = calculateTotals(renumberedItems, billData.gstRate);
      setBillData(prev => ({
        ...prev,
        items: renumberedItems,
        ...totals,
      }));
    }
  };

  const handleSave = () => {
    // Validation
    const errors: string[] = [];
    
    if (!billData.clientId) {
      errors.push('Please select a company');
    }
    
    if (!billData.invoiceNumber.trim()) {
      errors.push('Invoice number is required');
    }
    
    if (!billData.invoiceDate) {
      errors.push('Invoice date is required');
    }
    
    if (!billData.workOrderNumber.trim()) {
      errors.push('Work order number is required');
    }
    
    if (!billData.clientName.trim()) {
      errors.push('Client name is required');
    }
    
    if (!billData.clientAddress.trim()) {
      errors.push('Client address is required');
    }
    
    if (!billData.clientGSTN.trim()) {
      errors.push('Client GSTN is required');
    }
    
    // Check if any items have valid amounts
    const hasValidItems = billData.items.some(item => item.rate > 0 && item.qty > 0);
    if (!hasValidItems) {
      errors.push('At least one item with valid quantity and rate is required');
    }
    
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }
    
    const totals = calculateTotals(billData.items, billData.gstRate);
    let finalBillData = {
      ...billData,
      ...totals,
      createdAt: billData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // ✅ CRITICAL FIX: Remove id for NEW bills to prevent "Bill ID required for update" error
    if (!initialData?.id) {
      // Create a copy without the id property
      const { id, ...billWithoutId } = finalBillData as any;
      onSave(billWithoutId);
    } else {
      // This is an update - keep the id
      onSave(finalBillData);
    }
  };

  const { subTotal, cgstAmount, sgstAmount, totalGstAmount, totalAmount } = calculateTotals(billData.items, billData.gstRate);

  return (
   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-avis-blue to-avis-dark text-white p-8 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="bg-white p-3 rounded-xl shadow-lg">
                <svg className="w-8 h-8 text-avis-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold">Generate Bill</h2>
                <p className="text-blue-100 mt-1">Create a new bill for your client</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-3xl font-light transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-8">

          <div className="space-y-8">
            {/* Client Selection Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="bg-green-500 text-white p-2 rounded-lg mr-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Client Selection</h3>
              </div>
              <div ref={companySearchRef} className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Search & select client company
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={companySearchQuery}
                    onChange={(e) => {
                      setCompanySearchQuery(e.target.value);
                      setCompanyListOpen(true);
                    }}
                    onFocus={() => setCompanyListOpen(true)}
                    placeholder="Type to search companies..."
                    className="w-full border-2 border-gray-200 rounded-xl pl-12 pr-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900 font-medium"
                  />
                </div>
                {companyListOpen && (
                  <ul className="absolute z-10 w-full mt-1 max-h-60 overflow-auto rounded-xl border-2 border-gray-200 bg-white shadow-lg py-1">
                    {filteredCompanies.length === 0 ? (
                      <li className="px-4 py-3 text-gray-500 text-sm">No companies found</li>
                    ) : (
                      filteredCompanies.map((company) => (
                        <li
                          key={company.id}
                          onClick={() => {
                            setSelectedClient(company);
                            setCompanySearchQuery(company.name);
                            setCompanyListOpen(false);
                          }}
                          className={`px-4 py-3 cursor-pointer text-sm font-medium transition-colors ${
                            selectedClient?.id === company.id
                              ? 'bg-avis-blue text-white'
                              : 'text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {company.name}
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            </div>

            {/* Invoice Details Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="bg-blue-500 text-white p-2 rounded-lg mr-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Invoice Details</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Invoice Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billData.invoiceNumber}
                    onChange={(e) => handleBillDataChange('invoiceNumber', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                    placeholder="Enter invoice number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Invoice Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={billData.invoiceDate}
                    onChange={(e) => handleBillDataChange('invoiceDate', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Work Order Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billData.workOrderNumber}
                    onChange={(e) => handleBillDataChange('workOrderNumber', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                    placeholder="Enter work order number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Work Order Date
                  </label>
                  <input
                    type="text"
                    value={billData.workOrderDate}
                    onChange={(e) => handleBillDataChange('workOrderDate', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                    placeholder="Enter date or N/A"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Period Of Work
                  </label>
                  <input
                    type="text"
                    value={billData.periodOfWork || ''}
                    onChange={(e) => handleBillDataChange('periodOfWork', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                    placeholder="e.g., Nov-25"
                  />
                </div>
              </div>
            </div>

            {/* Client Details Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="bg-purple-500 text-white p-2 rounded-lg mr-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Client Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Client Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billData.clientName}
                    onChange={(e) => handleBillDataChange('clientName', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                    placeholder="Enter client name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Client GSTN <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billData.clientGSTN}
                    onChange={(e) => handleBillDataChange('clientGSTN', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900 font-mono"
                    placeholder="Enter client GSTN"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Client Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={billData.clientAddress}
                    onChange={(e) => handleBillDataChange('clientAddress', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900 resize-none"
                    rows={3}
                    placeholder="Enter client address"
                  />
                </div>
              </div>
            </div>

            {/* Items Table Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                  <div className="bg-orange-500 text-white p-2 rounded-lg mr-3">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Bill Items</h3>
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="bg-gradient-to-r from-avis-blue to-avis-dark text-white px-6 py-3 rounded-xl hover:from-avis-dark hover:to-avis-blue transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add Item</span>
                </button>
              </div>
              <div className="w-full overflow-hidden">
                <table className="w-full table-fixed divide-y divide-gray-200 bg-white rounded-xl shadow-sm">
                  <thead className="bg-gradient-to-r from-avis-blue to-avis-dark">
                    <tr>
                      <th className="w-[5%] px-2 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                        <div className="flex items-center justify-center space-x-1">
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                          </svg>
                          <span>Sr No</span>
                        </div>
                      </th>
                      <th className="w-[22%] px-2 py-5 text-left text-sm font-bold text-white uppercase tracking-wider">
                        <div className="flex items-center space-x-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>Description</span>
                        </div>
                      </th>
                      <th className="w-[10%] px-2 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                        <div className="flex items-center justify-center space-x-1">
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>SAC</span>
                        </div>
                      </th>
                      <th className="w-[13%] px-2 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                        <div className="flex items-center justify-center space-x-1">
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 8h6m-6 4h6m-6 4h6" />
                          </svg>
                          <span>Qty</span>
                        </div>
                      </th>
                      <th className="w-[18%] px-2 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                        <div className="flex items-center justify-center space-x-1">
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          <span>Rate</span>
                        </div>
                      </th>
                      <th className="w-[18%] px-2 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                        <div className="flex items-center justify-center space-x-1">
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span>Total</span>
                        </div>
                      </th>
                      <th className="w-[14%] px-2 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                        <div className="flex items-center justify-center space-x-1">
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Action</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billData.items.map((item, index) => (
                      <tr key={index} className="hover:bg-blue-50 transition-colors">
                        <td className="px-2 py-4 text-center">
                          <div className="bg-gradient-to-br from-avis-blue to-avis-dark text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm mx-auto">
                            {item.srNo}
                          </div>
                        </td>
                        <td className="px-2 py-4">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="w-full min-w-0 border-2 border-gray-300 rounded-xl px-3 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-all duration-200 bg-white shadow-sm hover:shadow-md text-gray-900 font-medium"
                            placeholder="Enter item description"
                          />
                        </td>
                        <td className="px-2 py-4">
                          <input
                            type="text"
                            value={item.sac}
                            onChange={(e) => handleItemChange(index, 'sac', e.target.value)}
                            className="w-full min-w-0 border-2 border-gray-300 rounded-xl px-3 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-all duration-200 bg-white shadow-sm hover:shadow-md text-gray-900 font-mono text-sm"
                            placeholder="SAC Code"
                          />
                        </td>
                        <td className="px-2 py-4">
                          <input
                            type="number"
                            value={item.qty}
                            onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                            className="w-full min-w-0 border-2 border-gray-300 rounded-xl px-3 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-all duration-200 bg-white shadow-sm hover:shadow-md text-gray-900 font-semibold text-center"
                            min="0"
                            step="0.01"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-4">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                            className="w-full min-w-0 border-2 border-gray-300 rounded-xl px-3 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-all duration-200 bg-white shadow-sm hover:shadow-md text-gray-900 font-semibold text-center text-base"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-2 py-4 text-right">
                          <div className="bg-gradient-to-r from-green-100 to-green-50 border-2 border-green-200 rounded-xl px-3 py-3 text-green-800 font-bold text-sm min-w-0">
                            ₹{formatNumber(item.totalAmount)}
                          </div>
                        </td>
                        <td className="px-2 py-4 text-center">
                          {billData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-800 p-3 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                              title="Remove item"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals Section */}
            <div className="bg-gradient-to-br from-avis-blue to-avis-dark rounded-xl p-6 text-white">
              <div className="flex items-center mb-6">
                <div className="bg-white p-2 rounded-lg mr-3">
                  <svg className="w-6 h-6 text-avis-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold">Bill Summary</h3>
              </div>
              <div className="flex justify-end">
                <div className="w-80 text-sm space-y-3">
                  <div className="flex justify-between py-2 border-b border-blue-300">
                    <span className="font-medium">Sub Total</span>
                    <span className="font-semibold">₹{formatNumber(subTotal)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-blue-300">
                    <span className="font-medium">Add CGST 9%</span>
                    <span className="font-semibold">₹{formatNumber(cgstAmount)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-blue-300">
                    <span className="font-medium">Add SGST 9%</span>
                    <span className="font-semibold">₹{formatNumber(sgstAmount)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-blue-300">
                    <span className="font-medium">Total Tax Amount</span>
                    <span className="font-semibold">₹{formatNumber(totalGstAmount)}</span>
                  </div>
                  <div className="flex justify-between py-3 border-t-2 border-white font-bold text-lg">
                    <span>Total Amount</span>
                    <span>₹{formatNumber(totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-8 border-t-2 border-gray-100">
            <button
              onClick={onClose}
              className="px-8 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-8 py-3 bg-gradient-to-r from-avis-blue to-avis-dark text-white rounded-xl hover:from-avis-dark hover:to-avis-blue transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
            >
              {isSaving ? (
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Save Bill</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillGenerator;